/**
 * FloatingTabBar — the bottom glass capsule with tab buttons.
 *
 * Port of the design-system mobile TabBar, made icon-agnostic: caller
 * supplies each tab's active + inactive icon as ReactNodes (so it works
 * with inline SVG, Ionicons, Lucide, whatever).
 *
 * Default positioning: `position: fixed` at the bottom of the viewport
 * with safe-area inset respect for iOS PWAs. Caller is responsible for:
 *   - adding lg:hidden (or similar) on the wrapper className if the bar
 *     should only show on mobile
 *   - reserving enough bottom scroll-padding on the page so the last
 *     item isn't hidden under the capsule
 *
 * Ported from bishopric-app-design-system/ui_kits/mobile/components.jsx.
 */
import type React from 'react'

type Tab = {
  key: string
  label: string
  activeIcon: React.ReactNode
  inactiveIcon: React.ReactNode
}

type FloatingTabBarProps = {
  tabs: Tab[]
  activeKey: string
  onChange: (key: string) => void
  className?: string
  style?: React.CSSProperties
}

export default function FloatingTabBar({
  tabs,
  activeKey,
  onChange,
  className = '',
  style = {},
}: FloatingTabBarProps) {
  return (
    <div
      className={className}
      style={{
        position: 'fixed',
        bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
        left: 16,
        right: 16,
        zIndex: 40,
        ...style,
      }}
    >
      <div
        style={{
          position: 'relative',
          borderRadius: 999,
          overflow: 'hidden',
          boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
        }}
      >
        {/* blur + tint */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backdropFilter: 'var(--glass-blur)',
            WebkitBackdropFilter: 'var(--glass-blur)',
            background: 'var(--glass-tint)',
          }}
        />
        {/* border + inner shine */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 999,
            border: '1px solid var(--glass-border)',
            boxShadow: 'inset 0 1px 0 var(--glass-shine)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'relative',
            display: 'flex',
            padding: 6,
            gap: 2,
          }}
        >
          {tabs.map((t) => {
            const isActive = activeKey === t.key
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => onChange(t.key)}
                style={{
                  flex: 1,
                  height: 52,
                  border: 'none',
                  background: isActive ? 'rgba(33,150,184,0.18)' : 'transparent',
                  borderRadius: 999,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                  padding: 0,
                  color: isActive ? 'var(--primary)' : 'var(--fg-2)',
                  transition: 'background 150ms',
                  fontFamily: 'inherit',
                }}
              >
                {isActive ? t.activeIcon : t.inactiveIcon}
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.1,
                  }}
                >
                  {t.label}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
